import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import {
  addSelectedField,
  addSelectedFilter,
  clearSelectedFields,
  clearSelectedFilters,
  clearTree,
  enterEditWidgetMode,
  exitEditWidgetMode,
  removeSelectedField,
  removeSelectedFilter,
  requestTreeFiltersToogle,
  SelectedFieldPayload,
  SelectedFilterPayload,
  selectMetricIndex,
  setEditWidgetGroupBy,
  setFilterSearch,
  setTree,
} from 'actions/dashboardEdit';
import { DashboardWidget, Tree } from 'types';
import { DataNode, Operator, ValueNode } from 'utils/parserTree';
import { v4 } from 'uuid';

type EditDashboardWidget = DashboardWidget & {
  filters_search: string[];
};

type DashboardState = {
  isEditing: boolean;
  widget: EditDashboardWidget | null;
  widgetIndex: number;
  metricEditIndex: number | null;

  selectedFilters: ValueNode[];
  selectedFields: DataNode[];
  filtersTree: Tree[];
  requestTreeFilters: boolean;
};

type DashboardReducer<P = void> = (
  state: DashboardState,
  payload: PayloadAction<P>
) => DashboardState;

const handleEnterEditMode: DashboardReducer<{ widget: DashboardWidget; widgetIndex: number }> = (
  state,
  { payload }
) => {
  const { widget, widgetIndex } = payload;

  return {
    ...state,
    isEditing: true,
    filtersTree:
      widget.metrics?.map(() => ({
        op: Operator.AND,
        data: [],
        id: v4(),
      })) || [],
    widget: {
      ...widget,
      // @ts-ignore
      filters_search: widget.metrics?.map((m) => m.filters_search || '') || [],
    },
    widgetIndex,
    metricEditIndex: null,
  };
};

const handleExitEditMode: DashboardReducer = (state) => ({
  ...state,
  isEditing: false,
  widget: null,
  widgetIndex: -1,
  metricEditIndex: null,
  selectedFilters: [],
  selectedFields: [],
  filtersTree: [],
});

const handleSetFiltersSearch: DashboardReducer<{
  filterSearch: string;
  metricIndex: number | null;
}> = (state, { payload }) => {
  if (!state.widget) return state;

  const { metricIndex, filterSearch } = payload;

  const newFilterSearches = [...state.widget.filters_search];
  if (metricIndex !== null) {
    newFilterSearches[metricIndex] = filterSearch || '';
  } else {
    newFilterSearches[0] = filterSearch || '';
  }

  return {
    ...state,
    widget: {
      ...state.widget,
      filters_search: newFilterSearches,
      metrics: state.widget.metrics?.map((m, idx) =>
        metricIndex !== null && idx !== metricIndex ? m : { ...m, filters_search: filterSearch }
      ),
    },
  };
};

const handleAddSelectedFilter: DashboardReducer<SelectedFilterPayload> = (state, { payload }) => {
  const selectedFilters = [...state.selectedFilters];
  if (
    selectedFilters.some(
      (filter) => filter.id !== payload.node.id && payload.node.parent !== filter.parent
    )
  ) {
    return {
      ...state,
      selectedFilters: [payload.node],
    };
  }
  return {
    ...state,
    selectedFilters: [...state.selectedFilters, payload.node],
  };
};

const handleRemoveSelectedFilter: DashboardReducer<SelectedFilterPayload> = (
  state,
  { payload }
) => {
  const selectedFilters = [...state.selectedFilters];
  const index = selectedFilters.findIndex((filter) => filter.id === payload.node.id);
  selectedFilters.splice(index, 1);
  return {
    ...state,
    selectedFilters,
  };
};

const handleSetTree: DashboardReducer<{ tree: Tree; metricIndex: number | null }> = (
  state,
  { payload }
) => ({
  ...state,
  filtersTree: state.filtersTree.map((f, idx) =>
    payload.metricIndex !== null && idx === payload.metricIndex ? payload.tree : f
  ),
});

const handleClearAllSelectedFilters: DashboardReducer = (state) => ({
  ...state,
  selectedFilters: [],
});

const handleAddSelectedField: DashboardReducer<SelectedFieldPayload> = (state, { payload }) => {
  const selectedFields = [...state.selectedFields];

  if (
    selectedFields.some(
      (field) => field.id !== payload.node.id && payload.node.parent !== field.parent
    )
  ) {
    return {
      ...state,
      selectedFields: [payload.node],
    };
  }

  return {
    ...state,
    selectedFields: [...state.selectedFields, payload.node],
  };
};

const handleRemoveSelectedField: DashboardReducer<SelectedFieldPayload> = (state, { payload }) => {
  const selectedFields = [...state.selectedFields];
  const index = selectedFields.findIndex((field) => field.id === payload.node.id);
  selectedFields.splice(index, 1);
  return {
    ...state,
    selectedFields,
  };
};

const handleClearAllSelectedFields: DashboardReducer = (state) => ({
  ...state,
  selectedFields: [],
});

const handleChangeRequestTree: DashboardReducer<{ value: boolean }> = (state, { payload }) => ({
  ...state,
  requestTreeFilters: payload.value,
});

const handleClearTree: DashboardReducer = (state) => {
  if (!state.widget) return state;

  return {
    ...state,
    filtersTree: [
      {
        op: Operator.AND,
        data: [],
        id: v4(),
      },
    ],
    widget: { ...state.widget, filters_search: [] },
  };
};

const handleSelectMetricIndex: DashboardReducer<number> = (state, { payload }) => ({
  ...state,
  metricEditIndex: payload,
});

const handleSetEditWidgetGroupBy: DashboardReducer<string> = (state, { payload }) => {
  if (state.widget) {
    return {
      ...state,
      widget: {
        ...state.widget,
        metrics: state.widget?.metrics?.map((w) => ({ ...w, unit_of_time: payload })),
      },
    };
  }
  return state;
};

const defaultState: DashboardState = {
  isEditing: false,
  widget: null,
  widgetIndex: -1,
  metricEditIndex: null,
  selectedFilters: [],
  selectedFields: [],
  filtersTree: [{ op: 'and', data: [], id: v4() }],
  requestTreeFilters: true,
};

const handlers = {
  [enterEditWidgetMode.toString()]: handleEnterEditMode,
  [exitEditWidgetMode.toString()]: handleExitEditMode,

  [setFilterSearch.toString()]: handleSetFiltersSearch,

  [addSelectedFilter.toString()]: handleAddSelectedFilter,
  [removeSelectedFilter.toString()]: handleRemoveSelectedFilter,
  [clearSelectedFilters.toString()]: handleClearAllSelectedFilters,
  [addSelectedField.toString()]: handleAddSelectedField,
  [removeSelectedField.toString()]: handleRemoveSelectedField,
  [clearSelectedFields.toString()]: handleClearAllSelectedFields,
  [setTree.toString()]: handleSetTree,
  [requestTreeFiltersToogle.toString()]: handleChangeRequestTree,
  [clearTree.toString()]: handleClearTree,
  [selectMetricIndex.toString()]: handleSelectMetricIndex,
  [setEditWidgetGroupBy.toString()]: handleSetEditWidgetGroupBy,
};

const dashboardEditReducer = createReducer(defaultState, handlers);

export default dashboardEditReducer;
